import type { ReactNode } from 'react'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router'

import { FlagIcon, NoteIconTestTube, SuccessCircledIcon } from '@slite/icons'

import { TooltipOnHover } from '../TooltipOnHover/TooltipOnHover'
import type { BaseLabelProps } from './BaseLabel'
import { BaseLabel } from './BaseLabel'

export type LabelProps = Omit<BaseLabelProps, 'children'> & {
  children?: ReactNode
  'data-test-id'?: string
  ref?: React.Ref<HTMLDivElement>
}

export function Label({
  children,
  'data-test-id': testId,
  ref,
  ...props
}: LabelProps) {
  return (
    <BaseLabel {...props}>
      {(_className, _style, renderContent) => (
        <div
          className={_className}
          style={_style}
          ref={ref}
          data-test-id={testId}
        >
          {renderContent(children)}
        </div>
      )}
    </BaseLabel>
  )
}

/** A state that went well, styled like the Verified state of a doc. */
export function SuccessLabel({
  children,
  'data-test-id': testId,
}: {
  children: ReactNode
  'data-test-id'?: string
}) {
  return (
    <Label
      variant="success"
      uppercase={false}
      icon={SuccessCircledIcon}
      iconClassName="margin-right-4"
      className="font-size-normal font-weight-semibold flexbox-no-shrink"
      data-test-id={testId}
    >
      {children}
    </Label>
  )
}

export const NewLabel = ({ size }: { size?: BaseLabelProps['size'] }) => {
  const intl = useIntl()
  return (
    <Label
      variant="primary"
      size={size ?? 'small'}
      className="margin-left-8 uppercase"
    >
      {intl.formatMessage({
        id: 'new',
        defaultMessage: 'new',
      })}
    </Label>
  )
}

export const BetaLabel = () => {
  const intl = useIntl()
  return (
    <Label variant="warn" size="small" className="margin-left-8 uppercase">
      {intl.formatMessage({
        id: 'beta',
        defaultMessage: 'beta',
      })}
    </Label>
  )
}

// Without this, it's hard for non-developers at Slite to know why they're seeing a feature,
// so they have to ask developers whether it's on a feature flag, and if so, which flag.
export function FeatureFlagLabel({ featureFlag }: { featureFlag: string }) {
  return (
    <TooltipOnHover
      positions={['top']}
      content={`Flag: ${featureFlag}`}
      target={({ ref, props }) => (
        <span ref={ref} {...props}>
          <Label variant="warn" size="small" className="margin-left-8">
            <FlagIcon size={12} className="margin-right-4" />
            <FormattedMessage id="gKNysUZN7Z" defaultMessage="Flag" />
          </Label>
        </span>
      )}
    />
  )
}

// Without this, it's hard for Slite customers to know why they're seeing a feature.
export function LabsLabel({
  labsFeature,
}: {
  labsFeature: {
    title: React.ReactElement
  }
}) {
  return (
    <TooltipOnHover
      positions={['top']}
      content={labsFeature.title}
      target={({ ref, props }) => (
        <span ref={ref} {...props}>
          <NavLink
            to={{
              pathname: `/settings/organization/labs`,
            }}
          >
            <Label
              variant="warn"
              size="small"
              icon={NoteIconTestTube}
              className="margin-left-8"
            >
              <FormattedMessage id="46PFY953uT" defaultMessage="Labs" />
            </Label>
          </NavLink>
        </span>
      )}
    />
  )
}
